import { Controller, useFormContext } from 'react-hook-form';
import { StanAccordion, StanAccordionBody, StanAccordionHeader, StanAccordionItem } from '@/components';
import { SocialInput } from './SocialInput';
import type { SocialPlatform } from '../types/profile.types';
import { SOCIAL_PLATFORM_CONFIGS } from '../constants/socialPlatforms';
import type { ProfileFormValues } from '../validations/profileSchema';
import IcChevronDown from '@/assets/icons/ic-chevron-down.svg';
import { cn, createIconElement } from '@/lib';
import {
    useAccordionContext,
    useAccordionItemContext,
} from '@/components/core-components/StanAccordion/StanAccordion.context';

interface SocialLinksSectionProps {
    disabled?: boolean;
}

// Extract platforms from config - first 2 are primary, rest are "more"
const PRIMARY_PLATFORMS: SocialPlatform[] = SOCIAL_PLATFORM_CONFIGS.slice(0, 2).map(config => config.platform);
const MORE_PLATFORMS: SocialPlatform[] = SOCIAL_PLATFORM_CONFIGS.slice(2).map(config => config.platform);

function MoreSocialsHeader() {
    const { id } = useAccordionItemContext();
    const { isItemOpen } = useAccordionContext();
    const isOpen = isItemOpen(id);

    return (
        <>
            <span className='text-brand-main font-semibold tracking-wide'>MORE SOCIALS</span>
            <div className='bg-brand-main text-on-brand ml-auto size-5 rounded-full'>
                {createIconElement(IcChevronDown, {
                    className: cn('text-on-brand size-5 pt-px transition-transform duration-200 ease-out', {
                        'rotate-180 scale-110': isOpen,
                    }),
                })}
            </div>
        </>
    );
}

export function SocialLinksSection({ disabled = false }: SocialLinksSectionProps) {
    const {
        control,
        formState: { errors },
    } = useFormContext<ProfileFormValues>();

    return (
        <div className='space-y-6'>
            <div>
                <p className='text-semantic-fg-base text-body-lg mb-4 font-medium'>Social Links</p>

                <div className='space-y-4'>
                    {/* Always show Instagram and TikTok */}
                    {PRIMARY_PLATFORMS.map(platform => (
                        <Controller
                            key={platform}
                            name={`socialLinks.${platform}`}
                            control={control}
                            render={({ field }) => (
                                <SocialInput
                                    id={`social-${platform}`}
                                    name={field.name}
                                    platform={platform}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    disabled={disabled}
                                    error={!!errors.socialLinks?.[platform]}
                                    hintText={errors.socialLinks?.[platform]?.message}
                                />
                            )}
                        />
                    ))}

                    {/* MORE SOCIALS collapsible section */}
                    <div className='-mx-4 mt-4'>
                        <StanAccordion allowMultipleOpen={false}>
                            <StanAccordionItem id='more-socials'>
                                <StanAccordionHeader hideEndIcon className='py-0.5!'>
                                    <MoreSocialsHeader />
                                </StanAccordionHeader>
                                <StanAccordionBody className='p-4'>
                                    <div className='space-y-4'>
                                        {MORE_PLATFORMS.map(platform => (
                                            <Controller
                                                key={platform}
                                                name={`socialLinks.${platform}`}
                                                control={control}
                                                render={({ field }) => (
                                                    <SocialInput
                                                        id={`social-${platform}`}
                                                        name={field.name}
                                                        platform={platform}
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        onBlur={field.onBlur}
                                                        disabled={disabled}
                                                        error={!!errors.socialLinks?.[platform]}
                                                        hintText={errors.socialLinks?.[platform]?.message}
                                                    />
                                                )}
                                            />
                                        ))}
                                    </div>
                                </StanAccordionBody>
                            </StanAccordionItem>
                        </StanAccordion>
                    </div>
                </div>
            </div>
        </div>
    );
}
